import React, { useEffect, useState } from 'react';

import ToggleRow from './ToggleRow';
import { kindTakesFiles, previewShareLinkFiles } from '../../services/shareLinks';
import { includesFilesText, restrictedFilesText } from '../../utils/publicFiles';

/**
 * What a view or item link would disclose in files, asked of the API's
 * preview whenever the target changes: `{ total, restricted }`, or null while
 * it is asked, when it fails, or for a kind that carries no files. A failed
 * count says nothing — the switch still works, and the API counts again
 * whenever the link is read.
 */
export function useShareLinkFilesCount(collectionId, { kind = 'view', query = '', itemId = null, enabled = true } = {}) {
	const [count, setCount] = useState(null);
	useEffect(() => {
		setCount(null);
		if (!enabled || !collectionId || !kindTakesFiles(kind)) return undefined;
		let active = true;
		previewShareLinkFiles(collectionId, { kind, query: query || undefined, item_id: itemId || undefined })
			.then((result) => { if (active) setCount(result); })
			.catch(() => { if (active) setCount(null); });
		return () => { active = false; };
	}, [collectionId, kind, query, itemId, enabled]);
	return count;
}

/**
 * The G15 note (the owner's: "the sharer is told if files behind it are not
 * downloadable, so nobody is surprised") — nothing when every file is.
 */
export function RestrictedFilesNote({ count, className = '' }) {
	const text = restrictedFilesText(count);
	if (!text) return null;
	return <p className={`include-files__note${className ? ` ${className}` : ''}`} data-testid="restricted-files-note">{text}</p>;
}

/**
 * "Include files" in the dialogs that make a VIEW or ITEM link (PR 10c; the
 * owner's rulings F10, G13, G15): one ToggleRow, ON for a new link, whose
 * description is the preview's count — "Includes 37 files on these items." —
 * so the choice is never silent, and under it the G15 note when some of
 * those files are listed by name only. Card and intake links never render it.
 */
export default function IncludeFilesField({
	collectionId,
	kind = 'view',
	query = '',
	itemId = null,
	checked,
	onChange,
	className = '',
}) {
	const count = useShareLinkFilesCount(collectionId, { kind, query, itemId });
	if (!kindTakesFiles(kind)) return null;
	const description = checked
		? (includesFilesText(count) || 'Visitors see the files on these items.')
		: "Visitors won't see the files on these items.";
	return (
		<div className={`include-files${className ? ` ${className}` : ''}`}>
			<ToggleRow
				first
				label="Include files"
				description={description}
				checked={checked}
				onChange={onChange}
			/>
			{checked ? <RestrictedFilesNote count={count} /> : null}
		</div>
	);
}
